import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Sparkles, Copy, ExternalLink, Mail, MessageSquare, Hash, Send, Image, Download } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/kaizen";

const PROGRAM_ACCENT = {
  "mini-competitions": "border-red-500/40 bg-[#D90429]/200/5",
  "powerkick-her": "border-pink-500/40 bg-pink-500/5",
  "prive": "border-amber-500/40 bg-amber-500/5",
};

function copyText(label, text) {
  navigator.clipboard.writeText(text || "");
  toast.success(`${label} copié !`);
}

function CopyBlock({ icon: Icon, title, text, testid }) {
  if (!text) return null;
  return (
    <div className="border border-zinc-800 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Icon size={15} /> {title}</span>
        <button onClick={() => copyText(title, text)} data-testid={`${testid}-copy`}
          className="flex items-center gap-1 text-xs font-semibold text-[#D90429] hover:underline">
          <Copy size={13} /> Copier
        </button>
      </div>
      <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans">{text}</pre>
    </div>
  );
}

export default function CampagneLes3Module() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posters, setPosters] = useState({});
  const [posterBusy, setPosterBusy] = useState(null);

  const loadPrograms = async () => {
    try {
      const { data } = await api.get(`/campagne-les3/programs?base_url=${encodeURIComponent(window.location.origin)}`);
      setPrograms(data.programs || []);
    } catch (e) { toast.error("Erreur de chargement des programmes."); }
  };

  useEffect(() => { loadPrograms(); }, []);

  const generate = async (programId) => {
    setSelected(programId); setResult(null); setBusy(true);
    try {
      const { data } = await api.post("/campagne-les3/generate", {
        program_id: programId, base_url: window.location.origin,
      });
      setResult(data);
      toast.success("Campagne générée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la génération.");
    } finally { setBusy(false); }
  };

  const c = result?.content || {};

  const prefillEmail = () => {
    if (!c.email) return;
    localStorage.setItem("prefill_email", JSON.stringify({
      subject: c.email.subject || "", message: c.email.body || "",
    }));
    toast.success("Courriel transféré vers Diffusion membres");
    navigate("/app/diffusion-courriel");
  };
  const prefillSms = () => {
    if (!c.sms) return;
    localStorage.setItem("prefill_sms", JSON.stringify({ message: c.sms || "" }));
    toast.success("SMS transféré vers Diffusion SMS");
    navigate("/app/diffusion-sms");
  };

  const generatePoster = async (programId) => {
    setPosterBusy(programId);
    try {
      const { data } = await api.post("/campagne-les3/poster", { program_id: programId });
      setPosters((p) => ({ ...p, [programId]: data.image }));
      toast.success("Affiche générée !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la génération de l'affiche.");
    } finally { setPosterBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="campagne-les3-module">
      <PageHeader title="📣 Campagne « Les 3 » — Été 2026"
        subtitle="Génère une séquence multi-canal (courriel + relance, SMS + relance, réseaux) pour les 3 programmes d'été." icon={Megaphone} />

      <SectionCard title="Les 3 programmes" icon={Sparkles}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="les3-programs">
          {programs.map((p) => (
            <div key={p.id} className={`border rounded-xl p-4 flex flex-col ${PROGRAM_ACCENT[p.id] || "border-zinc-800"}`} data-testid={`program-${p.id}`}>
              <div className="text-2xl">{p.emoji}</div>
              <p className="font-bold text-zinc-100 mt-1">{p.name}</p>
              <p className="text-xs text-zinc-400 mt-1 flex-1">{p.pitch}</p>
              <a href={p.public_link} target="_blank" rel="noreferrer" data-testid={`program-link-${p.id}`}
                className="flex items-center gap-1 text-xs text-[#D90429] font-semibold mt-2 hover:underline break-all">
                <ExternalLink size={12} /> {p.public_link}
              </a>
              <button onClick={() => generate(p.id)} disabled={busy} data-testid={`program-generate-${p.id}`}
                className="mt-3 flex items-center justify-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-3 py-2 rounded-lg text-sm disabled:opacity-50">
                <Sparkles size={14} /> {busy && selected === p.id ? "Génération…" : "Générer la campagne"}
              </button>
              <button onClick={() => generatePoster(p.id)} disabled={posterBusy === p.id} data-testid={`program-poster-${p.id}`}
                className="mt-2 flex items-center justify-center gap-2 bg-zinc-800 border border-zinc-700 hover:bg-zinc-800/60 text-zinc-200 font-semibold px-3 py-2 rounded-lg text-sm disabled:opacity-50">
                <Image size={14} /> {posterBusy === p.id ? "Création de l'affiche…" : "🎨 Générer une affiche"}
              </button>
              {posters[p.id] && (
                <div className="mt-3" data-testid={`program-poster-img-${p.id}`}>
                  <img src={posters[p.id]} alt={`Affiche ${p.name}`} className="rounded-lg border border-zinc-800 w-full" />
                  <a href={posters[p.id]} download={`affiche-${p.id}.png`} data-testid={`program-poster-download-${p.id}`}
                    className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold text-[#D90429] hover:underline">
                    <Download size={13} /> Télécharger l'affiche
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>

      {busy && !result && (
        <div className="text-center text-sm text-zinc-400 py-6" data-testid="les3-loading">✨ L'IA rédige ta séquence… (quelques secondes)</div>
      )}

      {result && (
        <SectionCard title={`Séquence générée — ${result.program_name}`} icon={Send}>
          <div className="flex items-center gap-2 mb-4 text-xs text-zinc-400">
            <ExternalLink size={13} /> Lien inclus : <a href={result.public_link} target="_blank" rel="noreferrer" className="text-[#D90429] font-semibold break-all">{result.public_link}</a>
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="les3-prefill-actions">
            <button onClick={prefillEmail} disabled={!c.email} data-testid="les3-prefill-email"
              className="flex items-center gap-2 bg-[#D90429] hover:bg-[#b8031f] text-white font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              <Mail size={15} /> Pré-remplir Diffusion courriel
            </button>
            <button onClick={prefillSms} disabled={!c.sms} data-testid="les3-prefill-sms"
              className="flex items-center gap-2 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              <MessageSquare size={15} /> Pré-remplir Diffusion SMS
            </button>
          </div>
          <div className="space-y-3" data-testid="les3-result">
            <CopyBlock icon={Mail} title="Courriel" text={c.email ? `Objet : ${c.email.subject}\n\n${c.email.body}` : ""} testid="les3-email" />
            <CopyBlock icon={Mail} title="Courriel — relance" text={c.email_relance ? `Objet : ${c.email_relance.subject}\n\n${c.email_relance.body}` : ""} testid="les3-email-relance" />
            <CopyBlock icon={MessageSquare} title="SMS" text={c.sms} testid="les3-sms" />
            <CopyBlock icon={MessageSquare} title="SMS — relance" text={c.sms_relance} testid="les3-sms-relance" />
            <CopyBlock icon={Hash} title="Légende réseaux sociaux" text={c.social_caption} testid="les3-social" />
            <CopyBlock icon={Sparkles} title="Accroches alternatives" text={(c.hooks || []).map((h, i) => `${i + 1}. ${h}`).join("\n")} testid="les3-hooks" />
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            💡 Pour diffuser : copie le courriel dans <strong>Diffusion membres</strong> et le SMS dans <strong>Diffusion SMS</strong>.
          </p>
        </SectionCard>
      )}
    </div>
  );
}
